export const MAX_CREDIT_AGE_YEARS = 69;
export const MAX_CREDIT_AGE_MONTHS = 11;
export const MAX_CREDIT_AGE_IN_MONTHS =
  MAX_CREDIT_AGE_YEARS * 12 + MAX_CREDIT_AGE_MONTHS;

const INVALID_DATE_MESSAGE =
  'No se puede validar la edad de la clienta. Verifica la fecha de nacimiento.';
const MAX_AGE_MESSAGE =
  'La clienta supera la edad máxima permitida de 69 años y 11 meses.';

const toDateOnly = (value) => {
  if (!value) {
    return null;
  }

  let rawValue = value;
  if (rawValue && typeof rawValue === 'object' && rawValue.iso) {
    rawValue = rawValue.iso;
  }

  if (typeof rawValue === 'string') {
    const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(rawValue);
    if (dateOnlyMatch) {
      const year = Number(dateOnlyMatch[1]);
      const month = Number(dateOnlyMatch[2]);
      const day = Number(dateOnlyMatch[3]);
      const date = new Date(Date.UTC(year, month - 1, day));

      if (
        date.getUTCFullYear() !== year ||
        date.getUTCMonth() !== month - 1 ||
        date.getUTCDate() !== day
      ) {
        return null;
      }

      return date;
    }
  }

  const date = rawValue instanceof Date ? rawValue : new Date(rawValue);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
  );
};

// La fecha de referencia representa el día calendario local del negocio.
// Usar getUTCDate() sobre new Date() puede adelantar el día en México después
// de las 18:00, cuando la hora UTC ya corresponde al día siguiente.
const toReferenceDateOnly = (value) => {
  if (!value) {
    return null;
  }

  if (typeof value === 'string' && /^(\d{4})-(\d{2})-(\d{2})/.test(value)) {
    return toDateOnly(value);
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
  );
};

const subtractMonths = (date, months) => {
  const sourceMonth = date.getUTCMonth() - months;
  const targetYear = date.getUTCFullYear() + Math.floor(sourceMonth / 12);
  const targetMonth = ((sourceMonth % 12) + 12) % 12;
  const sourceDay = date.getUTCDate();
  const lastDayOfTargetMonth = new Date(
    Date.UTC(targetYear, targetMonth + 1, 0)
  ).getUTCDate();

  return new Date(
    Date.UTC(
      targetYear,
      targetMonth,
      Math.min(sourceDay, lastDayOfTargetMonth)
    )
  );
};

export const getCreditAgeValidation = (
  birthDateValue,
  referenceDateValue = new Date()
) => {
  const birthDate = toDateOnly(birthDateValue);
  const referenceDate =
    toReferenceDateOnly(referenceDateValue) ||
    toReferenceDateOnly(new Date());

  if (!birthDate) {
    return {
      allowed: false,
      reason: 'invalid_birth_date',
      message: INVALID_DATE_MESSAGE,
    };
  }

  if (birthDate > referenceDate) {
    return {
      allowed: false,
      reason: 'future_birth_date',
      message: INVALID_DATE_MESSAGE,
    };
  }

  const minimumBirthDate = subtractMonths(
    referenceDate,
    MAX_CREDIT_AGE_IN_MONTHS
  );

  if (birthDate < minimumBirthDate) {
    return {
      allowed: false,
      reason: 'maximum_age_exceeded',
      message: MAX_AGE_MESSAGE,
    };
  }

  return {
    allowed: true,
    reason: null,
    message: '',
  };
};

export const isCreditAgeAllowed = (birthDateValue, referenceDateValue) =>
  getCreditAgeValidation(birthDateValue, referenceDateValue).allowed;
